/**
 * List-page memory: `useState` that survives leaving the page and coming back, for
 * this browser tab, plus the small helpers every list page needs around it.
 *
 * A list page's filters lived in plain component state, so following a link out
 * (a call sign, a client) and pressing Back put every filter back to its default.
 * Jesse, 2026-10-24: "some times I go to the construction-deadlines page, the
 * options seem to reset". On 2026-10-30 he asked for every page with filters to
 * behave the same way, through one shared mechanism rather than a copy per page.
 * This module is that mechanism:
 *
 * - `useSessionState(key, initial, isValid)` — one remembered value.
 * - `listStateKey('clients')` — namespaced keys, so pages cannot collide.
 * - `oneOf`, `textUpTo`, `isPageNumber`, `isIdFilter` — the validators pages share.
 * - `usePageResetOnChange` — back to page 1 when the filters change, but not on
 *   arrival, so a remembered page number survives.
 * - `usePageSize` — rows per page, offered by the shared Pagination.
 * - Sorting is remembered by `useTableSort`'s `persist` option, which uses this.
 *
 * sessionStorage, like the Search page's cache: per tab, gone when the tab closes,
 * never sent anywhere. Only filter values belong here, never anything
 * confidential. Storage can be unavailable or hold something stale from an older
 * build, so a value is used only when it has the default's type and passes
 * `isValid`; otherwise the default applies.
 */
import { useEffect, useRef, useState } from 'react'
import { useNavigationType } from 'react-router-dom'

export interface SessionStateOptions {
  /**
   * Ignore what was remembered and start from `initial`. For a page opened from a
   * deep link (the dashboard's "stale clients"), which must show exactly what the
   * link promised, not the link's filter on top of whatever was left set last time.
   */
  fresh?: boolean
}

/**
 * @param key  sessionStorage key, or null for plain, unremembered state (lets a
 *             shared hook make remembering optional without breaking hook order).
 */
export function useSessionState<T>(
  key: string | null,
  initial: T,
  isValid: (value: unknown) => boolean = () => true,
  options: SessionStateOptions = {},
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    if (key === null || options.fresh) return initial
    try {
      const raw = sessionStorage.getItem(key)
      if (raw !== null) {
        const parsed: unknown = JSON.parse(raw)
        if (typeof parsed === typeof initial && parsed !== null && isValid(parsed)) {
          return parsed as T
        }
      }
    } catch {
      // Unavailable (privacy mode) or unparseable: fall back to the default.
    }
    return initial
  })

  useEffect(() => {
    if (key === null) return
    try {
      sessionStorage.setItem(key, JSON.stringify(value))
    } catch {
      // Unavailable: the page still works, it just forgets on leaving.
    }
  }, [key, value])

  return [value, setValue]
}

/**
 * True when the page was reached by following a link, false on Back / Forward /
 * reload. A deep link's `fresh` start should apply to the click, not again every
 * time someone comes Back to the list it opened.
 */
export function useArrivedByLink(): boolean {
  return useNavigationType() !== 'POP'
}

/** `listStateKey('clients')('status')` → "clients.status". */
export function listStateKey(namespace: string) {
  return (name: string) => `${namespace}.${name}`
}

// --- Shared validators ---------------------------------------------------------

/** One of a fixed set of values (a select's options). */
export function oneOf(values: readonly unknown[]) {
  return (v: unknown) => values.includes(v)
}

/** Free text no longer than `max` (a search box). */
export function textUpTo(max: number) {
  return (v: unknown) => typeof v === 'string' && v.length <= max
}

export const isPageNumber = (v: unknown) => Number.isInteger(v) && (v as number) >= 1

/** '' (anyone), 'mine', 'none', or a numeric id: the assignee filters' values. */
export const isIdFilter = (v: unknown) =>
  typeof v === 'string' && (v === '' || v === 'mine' || v === 'none' || /^\d{1,10}$/.test(v))

/** A two-letter state code, or '' for all. */
export const isStateCode = (v: unknown) => typeof v === 'string' && /^[A-Z]{0,2}$/.test(v)

/** A short code-like value (service code, event type, …), or ''. */
export const isCode = (v: unknown) => typeof v === 'string' && /^[A-Za-z0-9_.:-]{0,64}$/.test(v)

// --- Paging --------------------------------------------------------------------

/** Rows-per-page choices; every list endpoint accepts up to at least 100. */
export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const

/**
 * A list's rows-per-page, remembered for the tab like its filters (Pagination
 * offers the choice).
 * @param namespace the page's list-state namespace, e.g. "clients"
 */
export function usePageSize(namespace: string, initial: number) {
  return useSessionState(listStateKey(namespace)('pageSize'), initial, oneOf(PAGE_SIZE_OPTIONS))
}

/**
 * Go back to page 1 whenever `viewKey` (the filters and sort, serialised) changes.
 *
 * Compares against the last view rather than skipping a first run, so arriving
 * with a remembered page number keeps it, and React's development double-run of
 * effects cannot reset it either.
 */
export function usePageResetOnChange(viewKey: string, setPage: (page: number) => void) {
  const lastView = useRef(viewKey)
  useEffect(() => {
    if (lastView.current === viewKey) return
    lastView.current = viewKey
    setPage(1)
  }, [viewKey, setPage])
}
